import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import Header from "../components/Header";
import ProgressBar from "../components/ProgressBar";
import ActionButton from "../components/ActionButton";
import PageTransition from "../components/PageTransition";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Play, User, Loader } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ScriptEditor from "../components/ScriptEditor";
import { toast } from "@/hooks/use-toast";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { setVideoScript, setVideoScriptApproved } from "@/store/features/videoSlice";
import { setListingData, setListingImages } from "@/store/features/listingSlice";
import { AppDispatch } from "@/store";
import Actor from "@/types/actor";
import { CREATE_DRAFT_VIDEO, CREATE_STRIPE_SESSION, GET_SCRAPING_JOB_DATA } from "@/integrations/apollo/operations";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAudioPlayback } from "@/hooks/useAudioPlayback";

const ScriptSelection = () => {
  const dispatch = useDispatch<AppDispatch>();
  const listing = useSelector((state: RootState) => state.listing);
  const selectedActor = useSelector((state: RootState) => state.video.actor) as Actor;
  const seletedScript = useSelector((state: RootState) => state.video.script);
  const videoMetadata = useSelector((state: RootState) => state.video);
  const navigate = useNavigate();

  const voice = selectedActor?.default_voice?.id;
  const [createDraftVideo] = useMutation(CREATE_DRAFT_VIDEO);
  const [createStripeSession] = useMutation(CREATE_STRIPE_SESSION);
  const [getScrapingJobData] = useMutation(GET_SCRAPING_JOB_DATA);

  const [wordCount, setWordCount] = useState(0);
  const MAX_WORDS = 150;

  // Use the audio playback hook
  const {
    generatingAudio,
    audioRef,
    generateAudio,
    handleAudioEnded,
    resetAudio,
    audioUrl,
  } = useAudioPlayback();

  useEffect(() => {
    if (listing.data?.script) {
      dispatch(setVideoScript(listing.data.script));
      setWordCount(listing.data.script.trim().split(/\s+/).filter(Boolean).length);
    }
  }, [listing]);

  useEffect(() => {
    const fetchScrapingJobData = async () => {
      const getData = async () => {
        try {
          const { data, errors } = await getScrapingJobData({ variables: { jobId: listing.jobId } });

          if (errors && errors.length > 0) {
            errors.forEach(error => {
              console.error('Error fetching scraping job data:', error);
            });

            throw new Error('Error fetching scraping job data');
          }

          // Wait for job to complete
          if (['pending', 'processing'].includes(data.retrieveScrapeWebsiteDataJobResults.extraction_job_status)) {
            return getData();
          }

          // calculate time taken to scrape
          const scrapingStartTime = parseInt(sessionStorage.getItem('scraping_start_time'));
          const scrapingEndTime = +new Date();
          const timeTaken = (scrapingEndTime - scrapingStartTime) / 1000;
          console.log(`Time taken to scrape: ${timeTaken} seconds`);

          return data;
        } catch (error) {
          toast({
            title: 'Error fetching scraping job data',
            description: 'Please try again.',
            variant: 'destructive',
          });

          navigate('/error?e=kev-001');
          return null;
        }
      };
      const listingData = await getData();

      if (listingData) {
        dispatch(setListingData(listingData.retrieveScrapeWebsiteDataJobResults));
        dispatch(setVideoScript(listingData.retrieveScrapeWebsiteDataJobResults.script));
        dispatch(setListingImages(listingData.retrieveScrapeWebsiteDataJobResults.image_urls));
      }
    };

    if (listing.jobId && !seletedScript.content) {
      fetchScrapingJobData();
    }
  }, [listing.jobId]);

  const handlePlayAudio = async () => {
    if (!voice || !seletedScript.content) return;

    await generateAudio(seletedScript.content, voice);
  };
  const handleScriptChange = (newScript: string) => {
    dispatch(setVideoScript(newScript));

    const words = newScript.trim().split(/\s+/).filter(Boolean);
    setWordCount(words.length);
    resetAudio();
  };
  const handleScriptApproved = (approved: boolean) => {
    dispatch(setVideoScriptApproved(approved));
  };
  const handleCheckout = async (e: React.MouseEvent) => {
    const isWordCountExceeded = wordCount > MAX_WORDS;

    if (!seletedScript.approved || isWordCountExceeded) {
      e.preventDefault();

      toast({
        title: "Action required",
        description: seletedScript.approved
          ? "Your script exceeds the maximum word count limit."
          : "Please listen to and approve your script before continuing.",
        variant: "destructive",
      });

      return false;
    }

    try {
      const { data: draftVideoData, errors: draftVideoErrors } = await createDraftVideo({
        variables: {
          script: seletedScript.content,
          image_urls: listing.images,
          heygen_template_id: null,
          avatar_id: selectedActor.id,
          voice_id: null,
          scraped_website_url: videoMetadata.listingUrl,
          scraped_data: listing.data.scraped_info,
          scrape_result_id: listing.jobId,
        }
      });

      if (draftVideoErrors && draftVideoErrors.length > 0) {
        console.error('Error creating draft video:', draftVideoErrors);
        return;
      }

      if (draftVideoData && draftVideoData.createVideoDraft) {
        const videoDraftId = draftVideoData.createVideoDraft.id;

        console.log('Draft video ID:', videoDraftId);

        const { data: stripeSessionData, errors: stripeSessionErrors } = await createStripeSession({
          variables: {
            video_id: videoDraftId,
          },
        });

        console.log('Stripe session data:', JSON.stringify(stripeSessionData));

        if (stripeSessionErrors && stripeSessionErrors.length > 0) {
          console.error('Error creating Stripe session:', stripeSessionErrors);
          return;
        }

        if (stripeSessionData && stripeSessionData.createStripeCheckoutSession) {
          const { checkout_session_url } = stripeSessionData.createStripeCheckoutSession;

          window.location.href = checkout_session_url;
        }
      }
    } catch (error) {
      console.error('Error creating draft video:', error);
    }
  };

  const isWordCountExceeded = wordCount > MAX_WORDS;
  const isContinueDisabled = !seletedScript.approved || isWordCountExceeded;

  const getButtonText = () => {
    if (!seletedScript.approved) {
      return "Approve Script Before Continuing";
    }
    if (isWordCountExceeded) {
      return "Script Exceeds Word Limit";
    }
    return "Continue to Checkout";
  };

  const actorImageUrl = selectedActor?.preview_image_url || selectedActor?.avatarUrl || '/placeholder.svg';

  return (
    <Layout>
      <PageTransition>
        <Header />
        <ProgressBar />

        <motion.div
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="text-center mb-6">
            <h2 className="font-bold tracking-tight mb-3 text-xl">STEP 3: Finalize Your Script</h2>
            <p className="text-muted-foreground text-balance">
              Edit your script, then listen and approve it to continue.
            </p>
          </div>

          <Card className="glass border-none shadow-sm overflow-hidden">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 items-center justify-between">
              <div className="flex items-center space-x-4 mb-4 md:mb-0">
                <Avatar className="h-16 w-16 border-2 border-primary/10">
                  <AvatarImage
                    src={actorImageUrl}
                    alt={selectedActor?.name || "Actor"}
                  />
                  <AvatarFallback className="bg-primary/5">
                    <User className="h-6 w-6 text-primary/70" />
                  </AvatarFallback>
                </Avatar>

                <div>
                  <h3 className="font-medium text-lg">Your Selected Actor</h3>
                  <p className="text-primary font-medium">{selectedActor?.name || "Actor"}</p>
                  {selectedActor.description && (
                    <p className="text-xs text-muted-foreground mt-1">{selectedActor.description}</p>
                  )}
                </div>
              </div>

              <div>
                {!audioUrl && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handlePlayAudio}
                    disabled={generatingAudio || !voice}
                    className="flex items-center gap-1 w-full"
                  >
                    {generatingAudio ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        Generating Audio...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4" />
                        Preview Script in Actor's Voice
                      </>
                    )}
                  </Button>
                )}

                { audioUrl && (
                  <audio ref={audioRef} controls onEnded={handleAudioEnded} className="w-full" />
                )}
              </div>
            </CardContent>
          </Card>

          <div className={`glass rounded-xl p-6 space-y-6 ${seletedScript.approved ? 'border-2 border-green-500' : ''}`}>
            <ScriptEditor
              script={seletedScript.content}
              onScriptChange={handleScriptChange}
            />
          </div>

          <div className="flex items-start space-x-2 mt-4 bg-muted/30 p-4 rounded-lg">
            <Checkbox
              id="approve-script"
              checked={seletedScript.approved}
              disabled={!seletedScript.content}
              onCheckedChange={checked => {
                handleScriptApproved(checked === true);
              }}
            />
            <div className="space-y-1 leading-none">
              <Label
                htmlFor="approve-script"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I have listened to the preview and approve this script
              </Label>
              <p className="text-xs text-muted-foreground">
                You must listen to and approve your script before continuing
              </p>
            </div>
          </div>

          <div className="pt-4 flex flex-col space-y-2">
            <Button
              onClick={handleCheckout}
              disabled={isContinueDisabled}
              className="primary"
            >
              {getButtonText()}
            </Button>

            <div className="flex justify-center">
              <ActionButton variant="back" to="/new-video/actor">
                Back to Actor Selection
              </ActionButton>
            </div>
          </div>

          <div className="glass rounded-xl p-6">
            <h3 className="font-medium mb-3">Tips for a great script</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 12H8" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <span>Keep it concise - scripts under 150 words work best</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 12H8" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <span>Highlight key features and unique selling points</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 12H8" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <span>Listen to the preview to ensure perfect pronunciation</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </PageTransition>
    </Layout>
  );
};

export default ScriptSelection;
